/**
 * Pure payout-split math (option A: ambassador is carved from Kalovate's cut).
 *
 * Priority waterfall, per order, on the PRE-TAX collected amount:
 *   1. creator  = creatorPct% of net        (unchanged from the legacy model)
 *   2. venue    = venuePct% of net           (only when the class has a venue)
 *   3. Kalovate's gross cut = net − creator − venue
 *   4. Stripe fee (and an optional platform floor) is RESERVED out of Kalovate's
 *      cut first, then the ambassador is paid from what remains, capped so the fee
 *      is always covered and Kalovate never goes negative.
 *   5. Kalovate net income = cut − fee − ambassador   (floored at 0)
 *
 * Reconciliation: creator + venue + ambassador + platform + fee === net
 * (except in the extreme edge where the fee alone exceeds Kalovate's cut — e.g. a
 * tiny order with a ~100% creator split — in which case Kalovate absorbs the
 * `platformShortfallCents`, exactly as the legacy model did by flooring at 0).
 *
 * Shared so the UI live-preview and the server snapshot use identical math.
 */

export interface AmbassadorRule {
  mode: 'pct_of_net' | 'fixed';
  /** Percent of net, e.g. 5 = 5%. Used when mode = 'pct_of_net'. */
  pct?: number | null;
  /** Fixed cents. Used when mode = 'fixed'. */
  fixedCents?: number | null;
}

export interface OrderSplitInput {
  /** Amount collected (post-discount), PRE-TAX. */
  netCents: number;
  /** Actual (or estimated) Stripe processing fee in cents. */
  stripeFeeCents: number;
  /** Creator share of net, e.g. 90. */
  creatorPct: number;
  /** Venue share of net, e.g. 15. Applied only when hasVenue. */
  venuePct?: number;
  hasVenue?: boolean;
  /**
   * Venue FIXED rental (per session) mode: the cents still owed on this session's
   * rental that this order may fund. When set, venue is paid min(this, creator pool)
   * — accrued across the session's tickets — instead of venuePct. Kalovate keeps its
   * usual (100 − creatorPct)% cut; the rental comes out of the creator's pool.
   */
  venueFixedCents?: number;
  /** Ambassador rule, if a class/creator has one assigned. */
  ambassador?: AmbassadorRule | null;
  /** Guaranteed Kalovate margin (in cents) kept on top of the Stripe fee before
   *  any ambassador share is paid. Default 0 (only the fee is reserved). */
  minPlatformFloorCents?: number;
}

export interface OrderSplit {
  processingFeeCents: number;
  creatorEarningsCents: number;
  venueEarningsCents: number;
  ambassadorEarningsCents: number;
  /** Kalovate net income (always ≥ 0). */
  platformFeeCents: number;
  /** True when the ambassador amount was reduced to protect the fee/floor. */
  ambassadorCapped: boolean;
  /** > 0 only when the Stripe fee exceeded Kalovate's whole cut; Kalovate absorbed it. */
  platformShortfallCents: number;
}

const n0 = (v: number | null | undefined): number => Math.max(Math.round(Number(v ?? 0)), 0);

export function computeOrderSplit(input: OrderSplitInput): OrderSplit {
  const net = n0(input.netCents);
  const stripeFee = n0(input.stripeFeeCents);
  const floor = n0(input.minPlatformFloorCents);

  let creator: number;
  let venue: number;
  let kalovateCut: number;

  if (input.venueFixedCents != null) {
    // Fixed venue rental: Kalovate keeps its usual (100 − creatorPct)% cut; the
    // rental accrues out of the creator's pool; creator gets the remainder.
    const platformPct = Math.max(100 - input.creatorPct, 0);
    kalovateCut = Math.min(Math.max(Math.round(net * (platformPct / 100)), 0), net);
    const creatorPool = Math.max(net - kalovateCut, 0);
    venue = Math.min(Math.max(Math.round(input.venueFixedCents), 0), creatorPool);
    creator = creatorPool - venue;
  } else {
    creator = Math.max(Math.round(net * (input.creatorPct / 100)), 0);
    venue = input.hasVenue ? Math.max(Math.round(net * ((input.venuePct ?? 0) / 100)), 0) : 0;
    // Kalovate's gross cut is whatever isn't creator/venue.
    kalovateCut = Math.max(net - creator - venue, 0);
  }

  // Reserve the Stripe fee (+ floor) BEFORE the ambassador — this is the guarantee.
  const availableForAmbassador = Math.max(kalovateCut - stripeFee - floor, 0);

  let desiredAmbassador = 0;
  if (input.ambassador) {
    desiredAmbassador =
      input.ambassador.mode === 'fixed'
        ? n0(input.ambassador.fixedCents)
        : Math.max(Math.round(net * (Number(input.ambassador.pct ?? 0) / 100)), 0);
  }
  const ambassador = Math.min(desiredAmbassador, availableForAmbassador);
  const ambassadorCapped = ambassador < desiredAmbassador;

  // Kalovate net = cut − fee − ambassador. Floored at 0; if the fee alone exceeded
  // the cut (independent of the ambassador), Kalovate absorbs the shortfall.
  const platformRaw = kalovateCut - stripeFee - ambassador;
  const platformFeeCents = Math.max(platformRaw, 0);
  const platformShortfallCents = platformRaw < 0 ? -platformRaw : 0;

  return {
    processingFeeCents: stripeFee,
    creatorEarningsCents: creator,
    venueEarningsCents: venue,
    ambassadorEarningsCents: ambassador,
    platformFeeCents,
    ambassadorCapped,
    platformShortfallCents,
  };
}
